import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/Button/Button';
import Input from '../../../../../components/Input/Input';
import Radio from '../../../../../components/Radio/Radio';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel, convertTo, EConversionSIToImperial } from '../../../../conversion';
import { setRockSlope } from '../../../store/project/projectslice';
import './RockSlope.scss';
import { IRockSlopeProps, IRockSlopeForm } from './IRockSlope';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import { Divider, Grid } from '@mui/material';
import { selectProjectInfo, selectMacro2 } from '../../../../store/projectInfo/projectInfoSlice';
import { updateValidation } from '../../../../Macro2/store/project/resultsSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../reduxService';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';

//create RockSlopeDefault based on IRockSlopeForm interface
export const RockSlopeDefault: IRockSlopeForm = {
    inclination: 65,
    slopeTotalHeight: 10,
    debrisAccumulationHeight: 1.5,
    debrisAccumulationWidth: 1.5,
    debrisAccumulationAngle: 0,
    debrisFrictionAngle: 20,
    debrisUnitWeight: 20,
    meshSlopeFrictionAngle: 18,
    snow: false,
    snowThickness: 0,
    snowUnitWeight: 4,
};

const RockSlope = (props: IRockSlopeProps) => {
    const projectInfo = useAppSelector(selectProjectInfo);
    const macro2 = useAppSelector(selectMacro2);

    const { t } = useTranslation();

    const defaultValues = macro2.project?.data?.rockSlope ?? RockSlopeDefault;

    const dispatch = useAppDispatch();

    const dispatchRockSlope = (values: IRockSlopeForm) => {
        dispatch(setRockSlope(values));
    }

    const checkConversion = (value: number, conversion: EConversionSIToImperial) => {
        return projectInfo.data?.unit == 'Metric' ? value : convertTo(value, conversion);
    }

    const ProjectSchema = Yup.object().shape({
        inclination: Yup.number().min(30).max(90).required().label(t('rock_slope_macro2_slope_inclination', { unit_measure: '' })),
        slopeTotalHeight: Yup.number().min(0.01).required().label(t('rock_slope_macro2_slope_total_height', { unit_measure: '' })),
        debrisAccumulationHeight: Yup.number().min(0.01).required().label(t('rock_slope_macro2_debris_accumulation_height', { unit_measure: '' })),
        debrisAccumulationWidth: Yup.number().min(0.01).required().label(t('rock_slope_macro2_debris_accumulation_width', { unit_measure: '' })),
        debrisAccumulationAngle: Yup.string().label(t('rock_slope_macro2_debris_accumulation_angle', { unit_measure: '' })),
        debrisFrictionAngle: Yup.number().min(0.01).required().label(t('rock_slope_macro2_debris_friction_angle', { unit_measure: '' })),
        debrisUnitWeight: Yup.number().min(0.01).required().label(t('rock_slope_macro2_debris_unit_weight', { unit_measure: '' })),
        meshSlopeFrictionAngle: Yup.number().min(0.01).required().label(t('rock_slope_macro2_mesh_slope_friction_angle', { unit_measure: '' })),
        snow: Yup.boolean().label(t('rock_slope_macro2_snow', { unit_measure: '' })),
        snowThickness: Yup.number().when('snow', ([snow], sch) => {
            if (snow) return Yup.number().min(0).required().label(t('rock_slope_macro2_snow_thickness', { unit_measure: '' }));
            return Yup.number().notRequired().label(t('rock_slope_macro2_snow_thickness', { unit_measure: '' }));
        }),
        snowUnitWeight: Yup.number().when('snow', ([snow], sch) => {
            if (snow) return Yup.number().min(0).required().label(t('rock_slope_macro2_snow_unit_weight', { unit_measure: '' }));
            return Yup.number().notRequired().label(t('rock_slope_macro2_snow_unit_weight', { unit_measure: '' }));
        }),
    });

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.data?.unit == 'Metric' ? 0 : 1] + ']';
    }

    const formikProps = useFormik({
        initialValues: defaultValues,
        validationSchema: ProjectSchema,
        onSubmit: (values, { setSubmitting }) => {
            dispatchRockSlope(values);
            setSubmitting(false);
        },
    });

    useEffect(() => {
        //console.log('values', formikProps.values);
        dispatchRockSlope(formikProps.values);
    }, [formikProps.values]);

    const currentResults = useAppSelector((state: RootState) => {
        return { ...state.Macro2.results.data };
    });
    useEffect(() => {
        if (currentResults.validation?.startValidation && !currentResults.validation?.rockSlope) {
            formikProps.submitForm();
            formikProps.validateForm().then((errors) => {
                if (currentResults.validation)
                    dispatch(updateValidation({
                        ...currentResults.validation,
                        rockSlope: true,
                        rockSlopeErrors: Object.keys(errors).length > 0,
                    }));
            });

        }
    }, [JSON.stringify(currentResults.validation)]);
    const debrisAccumulationAngle = useSelector((state: RootState) => state.Macro2.project.data?.rockSlope?.debrisAccumulationAngle);
    //console.log('debrisAccumulationAngle', debrisAccumulationAngle);

    useEffect(() => {
        //console.log('values', formikProps.values.debrisAccumulationAngle);
        if (debrisAccumulationAngle !== undefined) {
            formikProps.setFieldValue('debrisAccumulationAngle', debrisAccumulationAngle);
        }
    }, [debrisAccumulationAngle]);

    const maxLengthCheck = (object: any) => {
        if (object && object.target && object.target.value.length > object.target.maxLength) {
            object.target.value = object.target.value.slice(0, object.target.maxLength)
          }
        }
    
    return (
        <Grid container style={{ alignItems: 'center' }} className='style-form'>
            <FormikProvider value={formikProps}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_macro2_section_slope')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_slope_inclination', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                        <FieldNumber step={0.01} name='inclination' className='field' disabled={props.readOnly} />
                        <ErrorMessage name='inclination' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_slope_inclination_symbol')}</label>
                    </div>

                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_slope_total_height', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                        <FieldNumber step={0.01} name='slopeTotalHeight' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='slopeTotalHeight' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_slope_total_height_symbol')}</label>
                    </div>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_macro2_section_debris')}</label>
                    </Grid>

                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_debris_accumulation_height', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                        <FieldNumber step={0.01} name='debrisAccumulationHeight' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='debrisAccumulationHeight' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_debris_accumulation_height_symbol')}</label>
                    </div>

                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_debris_accumulation_width', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                        <FieldNumber step={0.01} name='debrisAccumulationWidth' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='debrisAccumulationWidth' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_debris_accumulation_width_symbol')}</label>
                    </div>

                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_debris_accumulation_angle', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                        <FieldNumber disabled step={0.01} name='debrisAccumulationAngle' className='d-flex justify-content-between align-items-center' />
                        <ErrorMessage name='debrisAccumulationAngle' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_debris_accumulation_angle_symbol')}</label>
                    </div>

                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_debris_friction_angle', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                        <FieldNumber step={0.01} name='debrisFrictionAngle' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='debrisFrictionAngle' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_debris_friction_angle_symbol')}</label>
                    </div>

                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_debris_unit_weight', { unit_measure: getMeasurementUnit(conversionLabel.kNm3_lbfft3) })}</label>
                        <FieldNumber step={0.01} name='debrisUnitWeight' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='debrisUnitWeight' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_debris_unit_weight_symbol')}</label>
                    </div>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_macro2_section_friction')}</label>
                    </Grid>

                    <Grid item xs={11}>
                        <label className='formLabel'>{t('rock_slope_macro2_mesh_slope_friction_angle', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                        <FieldNumber step={0.01} name='meshSlopeFrictionAngle' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                        <ErrorMessage name='meshSlopeFrictionAngle' component='span' className="text-danger" />
                    </Grid>
                    <div className="symbol-wrap">
                        <label className='symbol'>{t('rock_slope_macro2_mesh_slope_friction_angle_symbol')}</label>
                    </div>


                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('rock_slope_macro2_section_snow')}</label>
                    </Grid>

                    <Grid item xs={12}>
                        <label>{t('rock_slope_macro2_snow', { unit_measure: '' })}</label>
                        <Field
                            type="checkbox"
                            name="snow"
                            disabled={props.readOnly}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const isChecked = e.target.checked;
                                formikProps.setFieldValue('snow', isChecked);
                                if (!isChecked) {
                                    formikProps.setFieldValue('snowThickness', RockSlopeDefault.snowThickness);
                                    formikProps.setFieldValue('snowUnitWeight', RockSlopeDefault.snowUnitWeight);
                                }
                            }}
                        />
                        <ErrorMessage name="snow" component="div" className="text-danger" />
                    </Grid>
                    {formikProps.values.snow && (
                        <>
                            <Grid item xs={11} style={{ paddingLeft: 48 }}>
                                <label className='formLabel'>{t('rock_slope_macro2_snow_thickness', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                                <FieldNumber step={0.01} name='snowThickness' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                                <ErrorMessage name='snowThickness' component='span' className="text-danger" />
                            </Grid>
                            <div className="symbol-wrap">
                                <label className='symbol'>{t('rock_slope_macro2_snow_thickness_symbol')}</label>
                            </div>

                            <Grid item xs={11} style={{ paddingLeft: 48 }}>
                                <label className='formLabel'>{t('rock_slope_macro2_snow_unit_weight', { unit_measure: getMeasurementUnit(conversionLabel.kNm3_lbfft3) })}</label>
                                <FieldNumber step={0.01} name='snowUnitWeight' className='d-flex justify-content-between align-items-center' disabled={props.readOnly} />
                                <ErrorMessage name='snowUnitWeight' component='span' className="text-danger" />
                            </Grid>
                            <div className="symbol-wrap">
                                <label className='symbol'>{t('rock_slope_macro2_snow_unit_weight_symbol')}</label>
                            </div>

                            <Grid item xs={12} style={{ paddingLeft: 48 }}>
                                <Divider />
                            </Grid>
                        </>
                    )}
                </Grid>
            </FormikProvider>
        </Grid>
    );
};

export default RockSlope;
